<script setup>
import { computed } from 'vue'
import dayjs from 'dayjs'

const props = defineProps({
  size: { type: String, default: null },
  loading: { type: Boolean, default: false },
  autoheight: { type: Boolean, default: true },
  img: { type: [Object, String], default: null },
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  date: { type: [String, Number], default: '' },
  isVideo: { type: Boolean, default: false },
  titleTag: { type: String, default: 'h3' },
  backgroundc: { type: String, default: '#F8F9FA' },
  playIcon: {
    type: Object,
    default: () => {
      return {}
    }
  },
  link: {
    type: Object,
    default() {
      return { url: '', rel: '', target: '' }
    }
  }
})

defineEmits(['tracking'])

const isMobile = computed(() => ['s', 'xs'].includes(props.size))
const dateTime = computed(() => {
  const time = props.date
  if (time && dayjs(time).isValid()) {
    return dayjs.unix(time).format('YYYY-MM-DD HH:mm')
  } else {
    return ''
  }
})
</script>

<template>
  <div
    class="tvb-article-ghost-card"
    :class="[size ? 'card-' + size : '', { 'card-autoheight': autoheight }]"
  >
    <div v-if="loading" class="tvb-article-card__skeleton">
      <div class="tvb-article-card__image tvb-hairline--surround">
        <div class="tvb-article-card__image--wrap">
          <tvb-image class="tvb-article-card__cover" src="" backgroundc="#F5F6F7"></tvb-image>
        </div>
      </div>
      <div class="tvb-article-card__skeleton--info">
        <div class="tvb-article-card__skeleton--text"></div>
        <div
          class="tvb-article-card__skeleton--text"
          style="width: 62.5%; margin-top: var(--gc-info-margin-top)"
        ></div>
      </div>
    </div>
    <tvb-router-link
      v-else
      :to="link.url"
      :linkRel="link.rel"
      :linkTarget="link.target"
      :class="{ 'disable-hover': isMobile }"
      @click="$emit('tracking')"
    >
      <div
        class="tvb-article-card__image tvb-hairline--surround"
        :class="{ 'tvb-article-card__image--hover': !isMobile }"
      >
        <div class="tvb-article-card__image--wrap">
          <tvb-image
            class="tvb-article-card__cover"
            :src="img"
            :alt="title"
            :isVideo="isVideo"
            :playIcon="playIcon"
            :transform="isMobile ? false : true"
            :backgroundc="backgroundc"
          ></tvb-image>
        </div>
      </div>
    </tvb-router-link>
    <div class="tvb-article-card__info">
      <component :is="titleTag" class="clear-h tvb-article-card__info--tit" :title="title">
        <tvb-router-link
          :to="link.url"
          :linkRel="link.rel"
          :linkTarget="link.target"
          :class="{ 'disable-hover': isMobile }"
          @click="$emit('tracking')"
        >
          {{ title }}
        </tvb-router-link>
      </component>
      <div v-if="subtitle || date" class="tvb-article-card__info--bottom">
        <span class="tvb-article-card__info--author" :title="subtitle">{{ subtitle }}</span>
        <span v-if="date" class="tvb-article-card__info--date">{{ dateTime }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tvb-article-ghost-card {
  --gc-title-font-size: 16px;
  --gc-title-line-height: 22px;
  --gc-title-font-color: #333;
  --gc-subtitle-font-size: 13px;
  --gc-subtitle-line-height: 18px;
  --gc-subtitle-font-color: #818d95;
  --gc-info-margin-top: 8px;
  --gc-info-padding: 0px;
  --gc-subtitle-margin-top: 4px;
  --gc-cover-radio: 56.25%; // 如何计算值？ 9 / 16（高除以宽）
  --gc-hover-color: #0062a3;
  --gc-img-border-radius: 6px;
  position: relative;

  a {
    text-decoration: none;
    background-color: transparent;
    color: inherit;
    cursor: pointer;
    @include text-hover-before();
    &:visited {
      color: inherit;
    }
  }
  a:not(.disable-hover):hover {
    @include text-hover-active();
  }

  // img样式
  .tvb-article-card__image {
    position: relative;
    border-radius: var(--gc-img-border-radius);
    --tvb-hairline-border-radius: calc(2 * var(--gc-img-border-radius));
  }
  .tvb-article-card__image--wrap {
    padding-top: var(--gc-cover-radio);
    border-radius: inherit;
    cursor: pointer;
  }
  .tvb-article-card__cover {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: inherit;
    overflow: hidden;
    @include safari-overflow-hidden();
  }

  // 内容样式
  .tvb-article-card__info {
    margin-top: var(--gc-info-margin-top);
    padding: var(--gc-info-padding);
    min-height: calc(var(--gc-title-line-height) * 2);

    .tvb-article-card__info--tit {
      color: var(--gc-title-font-color);
      font-size: var(--gc-title-font-size);
      line-height: var(--gc-title-line-height);
      height: calc(2 * var(--gc-title-line-height));
      letter-spacing: 0.5px;
      word-break: break-all;
      @include line-text-ellipsis(2);

      & > a {
        color: var(--gc-title-font-color);
      }
    }

    .tvb-article-card__info--bottom {
      margin-top: var(--gc-subtitle-margin-top);
      color: var(--gc-subtitle-font-color);
      font-size: var(--gc-subtitle-font-size);
      display: flex;
      align-items: center;
      display: inline-flex;
    }

    .tvb-article-card__info--author {
      flex: 1;
      word-break: break-word;
      line-height: var(--gc-subtitle-line-height);
      @include line-text-ellipsis(1);
    }

    .tvb-article-card__info--date {
      margin-left: 12px;
      line-height: var(--gc-subtitle-line-height);
    }
  }

  // 动态高度样式
  &.card-autoheight {
    .tvb-article-card__info {
      min-height: auto;
      .tvb-article-card__info--tit {
        height: auto;
      }
    }
  }

  // 骨架屏样式
  .tvb-article-card__skeleton {
    .tvb-article-card__skeleton--info {
      margin-top: var(--gc-info-margin-top);
      padding: var(--gc-info-padding);
      .tvb-article-card__skeleton--text {
        height: 26px;
        background-color: #f0f1f2;
        border-radius: 4px;
      }
    }
  }
}
</style>
